import { z } from 'zod'

/**
------------------------------------------------------------------------------------------------------------------------------------------------
 * Types for SSW subscription packages and prices. These need to match what we have 'normalised' on the SSW project,
 * as these are used to cross-platform share data about recent subscription transactions that were incomplete.
 *
 * REF: https://github.com/West-Australian-Newspapers/ssw
 * PATH: src/Wan/ContingencyBundle/Entity/SubscriptionOfferApi/SubscriptionOffer.php
 * FUNCTION: normalisePackage()
------------------------------------------------------------------------------------------------------------------------------------------------
*/

// Matching price object as the below ref:
// https://github.com/West-Australian-Newspapers/ssw/blob/master/src/Wan/ContingencyBundle/Entity/SubscriptionOfferApi/SubscriptionPriceObject.php
export const sswSubscriptionPriceSchema = z.object({
    price: z.string().optional().nullable(),
    promoPrice: z.number().optional().nullable(),
    offerCode: z.string().optional().nullable(),
    packagePath: z.string().optional().nullable(),
    billingCycle: z.string().optional().nullable(),
    priceQualifier: z.string().optional().nullable(),
    googleSKU: z.string().optional().nullable(),
    cancellationMessage: z.string().optional().nullable(),
    additionalBenefits: z.array(z.string()).optional().nullable(),
    packageType: z.string().optional().nullable(),
    packageTitle: z.string().optional().nullable(),
    rateCode: z.string().optional().nullable(),
    weeklyPrice: z.number().optional().nullable(),
})
export type SswSubscriptionPrice = z.infer<typeof sswSubscriptionPriceSchema>

// Matching column position object as the below ref:
// https://github.com/West-Australian-Newspapers/ssw/blob/master/src/Wan/ContingencyBundle/Entity/SubscriptionOfferApi/SubscriptionColumnPosition.php
export const sswColumnPositionSchema = z.object({
    mobile: z.number().optional().nullable(),
    desktop: z.number().optional().nullable(),
})
export type SswColumnPosition = z.infer<typeof sswColumnPositionSchema>

// Matching package object as the below ref:
// https://github.com/West-Australian-Newspapers/ssw/blob/master/src/Wan/ContingencyBundle/Entity/SubscriptionOfferApi/SubscriptionOffer.php
export const sswSubscriptionPackageSchema = z.object({
    type: z.enum([
        'ssw-subscription',
        'radio-list-subscriptions',
        'single-subscription',
    ]),
    heading: z.string().optional().nullable(),
    subHeading: z.string().optional().nullable(),
    packageType: z.string().optional().nullable(),
    packageTheme: z.string().optional().nullable(),
    buttonStyle: z.string().optional().nullable(),
    benefitOverride: z.array(z.string()).optional().nullable(),
    displayOrder: z.number().optional().nullable(),
    ribbonTitle: z.string().optional().nullable(),
    ribbonOptions: z.string().optional().nullable(),
    isRecommended: z.boolean().optional().nullable(),
    prices: z.array(sswSubscriptionPriceSchema).optional().nullable(),
    columnPosition: sswColumnPositionSchema.optional().nullable(),
})
export type SswSubscriptionPackage = z.infer<
    typeof sswSubscriptionPackageSchema
>
